import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { errMsg, fmtDate, useApi } from "@/lib/api";
import { Btn, Table, Td } from "./AdminUi";
import { RichEditor } from "./blog/RichEditor";
import { SeoPanel } from "./blog/SeoPanel";
import { analyzeSeo, textOf } from "./blog/seoAnalysis";
import { toHtml } from "@/lib/blogHtml";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_url: "", cover_alt: "", category: "Guide", published: true,
  seo_title: "", meta_description: "", focus_keyword: "" };
const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="flex justify-between text-xs font-medium text-slate-400"><span>{label}</span>{hint && <span className="text-slate-500">{hint}</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function useUpload(title) {
  const api = useApi();
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("context", title || "");
    setUploading(true);
    try {
      const r = await api("post", "/admin/blog/image", fd);
      toast.success(`Immagine ottimizzata: ${r.original_kb} KB → ${r.size_kb} KB (WebP ${r.width}×${r.height}). Alt: «${r.alt}»`);
      return r;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    } finally {
      setUploading(false);
    }
  };
  return { upload, uploading };
}

function CoverPicker({ form, setForm, upload, uploading }) {
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const r = await upload(f);
    if (r) setForm((p) => ({ ...p, cover_url: r.url, cover_alt: r.alt }));
  };
  return (
    <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
      <label className="grid aspect-[16/9] cursor-pointer place-items-center overflow-hidden rounded-xl border border-dashed border-white/15 bg-[#1C1F2C] text-xs text-slate-400 hover:border-amber-500/50" data-testid="blog-cover-upload">
        {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : form.cover_url ? <img src={form.cover_url} alt={form.cover_alt} className="h-full w-full object-cover" /> : <span className="flex flex-col items-center gap-1"><ImagePlus className="h-5 w-5" />Copertina</span>}
        <input type="file" accept="image/*" hidden onChange={pick} data-testid="blog-cover-input" />
      </label>
      <div className="space-y-2">
        <Input data-testid="blog-cover" placeholder="…oppure incolla l'URL dell'immagine" value={form.cover_url} onChange={(e) => setForm((p) => ({ ...p, cover_url: e.target.value }))} className="bg-[#1C1F2C]" />
        <Input data-testid="blog-cover-alt" placeholder="Testo alternativo copertina (alt)" value={form.cover_alt} onChange={(e) => setForm((p) => ({ ...p, cover_alt: e.target.value }))} className="bg-[#1C1F2C]" />
      </div>
    </div>
  );
}

function PostEditor({ initial, onDone }) {
  const api = useApi();
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [slugTouched, setSlugTouched] = useState(!!initial.id);
  const [saving, setSaving] = useState(false);
  const { upload, uploading } = useUpload(form.title);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setTitle = (e) => { const v = e.target.value; setForm((p) => ({ ...p, title: v, slug: slugTouched ? p.slug : slugify(v) })); };

  const save = async (published) => {
    setSaving(true);
    try {
      const text = textOf(toHtml(form.content)).text;
      const auto = text.length > 157 ? text.slice(0, 157).replace(/\s+\S*$/, "") + "…" : text;
      const body = { ...form, published, slug: slugify(form.slug || form.title), excerpt: form.excerpt || form.meta_description || auto };
      if (body.excerpt.length < 10) throw new Error("Scrivi un riassunto o un testo più lungo prima di salvare");
      if (initial.id) await api("put", `/admin/blog/${initial.id}`, body);
      else await api("post", "/admin/blog", body);
      toast.success(published ? "Articolo pubblicato" : "Bozza salvata");
      await onDone();
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_400px]" data-testid="blog-editor-view">
      <div className="space-y-4 min-w-0">
        <Input data-testid="blog-title" placeholder="Titolo dell'articolo" value={form.title} onChange={setTitle}
          className="h-14 bg-[#1C1F2C] font-display text-2xl font-bold" />
        <Field label="Indirizzo (slug)" hint={`/blog/${form.slug || "…"}`}>
          <Input data-testid="blog-slug" value={form.slug} onChange={(e) => { setSlugTouched(true); setForm((p) => ({ ...p, slug: slugify(e.target.value) })); }} className="bg-[#1C1F2C]" />
        </Field>
        <RichEditor docKey={initial.id || "new"} value={form.content} onChange={(v) => setForm((p) => ({ ...p, content: v }))} onUpload={upload} uploading={uploading} />
        <Field label="Riassunto (mostrato nell'elenco del blog)" hint={`${form.excerpt.length}/320`}>
          <Textarea data-testid="blog-excerpt" rows={2} maxLength={320} value={form.excerpt} onChange={set("excerpt")} className="bg-[#1C1F2C]" />
        </Field>
      </div>
      <aside className="space-y-5">
        <div className="space-y-3 rounded-2xl border border-white/10 bg-[#12141C] p-5">
          <p className="font-semibold text-white">Pubblicazione</p>
          <label className="flex items-center gap-3 text-sm text-slate-300"><Switch data-testid="blog-published" checked={form.published} onCheckedChange={(v) => setForm((p) => ({ ...p, published: v }))} /> {form.published ? "Visibile sul sito" : "Bozza"}</label>
          <Field label="Categoria"><Input data-testid="blog-category" value={form.category} onChange={set("category")} className="bg-[#1C1F2C]" /></Field>
          <Field label="Immagine di copertina"><CoverPicker form={form} setForm={setForm} upload={upload} uploading={uploading} /></Field>
          <div className="flex flex-wrap gap-2 pt-1">
            <Btn variant="gold" testid="blog-save" onClick={() => save(true)} disabled={saving}>{initial.id ? "Aggiorna e pubblica" : "Pubblica"}</Btn>
            <Btn testid="blog-save-draft" onClick={() => save(false)} disabled={saving}>Salva bozza</Btn>
            <Btn testid="blog-cancel" onClick={onDone}>Chiudi</Btn>
          </div>
        </div>
        <div className="space-y-3 rounded-2xl border border-white/10 bg-[#12141C] p-5">
          <p className="font-semibold text-white">Impostazioni SEO</p>
          <Field label="Parola chiave principale"><Input data-testid="blog-keyword" placeholder="es. confronto prezzi online" value={form.focus_keyword} onChange={set("focus_keyword")} className="bg-[#1C1F2C]" /></Field>
          <Field label="Titolo SEO" hint={`${(form.seo_title || form.title).length}/60`}><Input data-testid="blog-seo-title" placeholder={form.title || "Se vuoto usa il titolo"} value={form.seo_title} onChange={set("seo_title")} className="bg-[#1C1F2C]" /></Field>
          <Field label="Meta description" hint={`${(form.meta_description || form.excerpt).length}/155`}>
            <Textarea data-testid="blog-meta" rows={3} placeholder="Se vuota usa il riassunto" value={form.meta_description} onChange={set("meta_description")} className="bg-[#1C1F2C]" />
          </Field>
        </div>
        <SeoPanel form={form} setForm={setForm} />
      </aside>
    </div>
  );
}

export function AdminBlog() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => setRows(await api("get", "/admin/blog")), [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Eliminare «${p.title}»?`)) return;
    try {
      await api("delete", `/admin/blog/${p.id}`);
      toast.success("Articolo eliminato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (editing) return <PostEditor initial={editing} onDone={async () => { await load().catch(() => {}); setEditing(null); }} />;
  return (
    <div className="space-y-6" data-testid="admin-blog">
      <Btn variant="gold" testid="blog-new" onClick={() => setEditing({})}><span className="inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Nuovo articolo</span></Btn>
      <Table testid="admin-blog-list" empty={!rows.length} head={["Articolo", "SEO", "Data", "Stato", "Azioni"]}>
        {rows.map((p) => {
          const score = analyzeSeo({ ...EMPTY, ...p }).score;
          return (
            <tr key={p.id}>
              <Td><p className="text-white">{p.title}</p><p className="text-xs text-slate-500">/blog/{p.slug}</p></Td>
              <Td><span data-testid={`blog-score-${p.id}`} className={`font-semibold ${score >= 75 ? "text-emerald-400" : score >= 50 ? "text-amber-400" : "text-red-400"}`}>{score}/100</span></Td>
              <Td>{fmtDate(p.created_at)}</Td>
              <Td>{p.published ? "Pubblicato" : "Bozza"}</Td>
              <Td><div className="flex gap-2"><Btn testid={`blog-edit-${p.id}`} onClick={() => setEditing(p)}>Modifica</Btn><Btn variant="warn" testid={`blog-delete-${p.id}`} onClick={() => remove(p)}>Elimina</Btn></div></Td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}
